import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Menu, X, Phone, Mail, ChevronRight, ArrowRight,
  Facebook, Instagram, Linkedin, Twitter, Youtube,
  Bike, Fuel, Smartphone, Shield, Settings, Wrench,
  Users, GraduationCap, Cpu, Heart, MessageCircle,
  Check, Send, Calendar, User, Tag, ChevronDown,
  MapPin, Clock, Award, Leaf, Zap, BatteryCharging, Share2
} from "lucide-react";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom Cursor Component - Delivery Bike
const CustomCursor = () => {
  const cursorRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(0);
  const [scrollDirection, setScrollDirection] = useState('down');
  const lastScrollY = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const scrollTimeout = useRef(null);

  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    const moveCursor = (e) => {
      cursor.style.left = `${e.clientX}px`;
      cursor.style.top = `${e.clientY}px`;
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    // Scroll detection for driving animation with speed tracking
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      const timeDiff = currentTime - lastScrollTime.current;
      const scrollDiff = Math.abs(currentScrollY - lastScrollY.current);
      
      // Calculate scroll speed (pixels per millisecond)
      const speed = timeDiff > 0 ? scrollDiff / timeDiff : 0;
      setScrollSpeed(Math.min(speed * 10, 3)); // Cap at 3 for max effect
      
      setScrollDirection(currentScrollY > lastScrollY.current ? 'down' : 'up');
      lastScrollY.current = currentScrollY;
      lastScrollTime.current = currentTime;
      
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      // Stop "driving" animation after scrolling stops
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
        setScrollSpeed(0);
      }, 150);
    };

    // Add hover effect for interactive elements
    const addHoverListeners = () => {
      const interactiveElements = document.querySelectorAll('a, button, input, textarea, select, [role="button"]');
      interactiveElements.forEach(el => {
        el.addEventListener('mouseenter', () => setIsHovering(true));
        el.addEventListener('mouseleave', () => setIsHovering(false));
      });
    };

    document.addEventListener('mousemove', moveCursor);
    document.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Add hover listeners after a short delay to ensure DOM is ready
    const timeout = setTimeout(addHoverListeners, 500);

    return () => {
      document.removeEventListener('mousemove', moveCursor);
      document.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [isVisible]);

  const size = isHovering ? 48 : 40;
  const tilt = isScrolling ? (scrollDirection === 'down' ? -8 : 8) : 0;
  const isFastScrolling = scrollSpeed > 1;

  return (
    <div
      ref={cursorRef}
      className={`custom-cursor ${isVisible ? 'visible' : ''} ${isHovering ? 'hovering' : ''} ${isScrolling ? 'driving' : ''}`}
      data-testid="custom-cursor"
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 99999,
        transform: `translate(-50%, -50%) rotate(${tilt}deg)`,
        transition: 'transform 0.15s ease-out, opacity 0.2s ease',
      }}
    >
      <svg 
        width={size + (isFastScrolling ? 30 : 0)} 
        height={size} 
        viewBox={isFastScrolling ? "-30 0 130 100" : "0 0 100 100"}
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ 
          transition: 'width 0.2s, height 0.2s',
          filter: isHovering 
            ? 'drop-shadow(0 0 10px rgba(30, 27, 75, 0.6))' 
            : isScrolling 
              ? 'drop-shadow(4px 2px 6px rgba(0, 0, 0, 0.4))' 
              : 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))'
        }}
      >
        {/* Speed Lines - appear when scrolling fast */}
        {isFastScrolling && (
          <g className="speed-lines">
            {/* Top speed line */}
            <line x1="-5" y1="35" x2="-25" y2="35" stroke="#1E1B4B" strokeWidth="3" strokeLinecap="round" opacity="0.7">
              <animate attributeName="x1" values="-5;-15;-5" dur="0.2s" repeatCount="indefinite"/>
              <animate attributeName="x2" values="-25;-35;-25" dur="0.2s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.7;0.3;0.7" dur="0.2s" repeatCount="indefinite"/>
            </line>
            
            {/* Middle speed line (longest) */}
            <line x1="-5" y1="50" x2="-30" y2="50" stroke="#1E1B4B" strokeWidth="4" strokeLinecap="round" opacity="0.8">
              <animate attributeName="x1" values="-5;-12;-5" dur="0.15s" repeatCount="indefinite"/>
              <animate attributeName="x2" values="-30;-40;-30" dur="0.15s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.8;0.4;0.8" dur="0.15s" repeatCount="indefinite"/>
            </line>
            
            {/* Bottom speed line */}
            <line x1="-5" y1="65" x2="-20" y2="65" stroke="#1E1B4B" strokeWidth="3" strokeLinecap="round" opacity="0.6">
              <animate attributeName="x1" values="-5;-10;-5" dur="0.25s" repeatCount="indefinite"/>
              <animate attributeName="x2" values="-20;-28;-20" dur="0.25s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.6;0.2;0.6" dur="0.25s" repeatCount="indefinite"/>
            </line>

            {/* Extra short lines for more speed effect */}
            <line x1="0" y1="42" x2="-12" y2="42" stroke="#312E81" strokeWidth="2" strokeLinecap="round" opacity="0.5">
              <animate attributeName="x2" values="-12;-18;-12" dur="0.18s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.5;0.2;0.5" dur="0.18s" repeatCount="indefinite"/>
            </line>
            <line x1="0" y1="58" x2="-15" y2="58" stroke="#312E81" strokeWidth="2" strokeLinecap="round" opacity="0.5">
              <animate attributeName="x2" values="-15;-22;-15" dur="0.22s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.5;0.2;0.5" dur="0.22s" repeatCount="indefinite"/>
            </line>
          </g>
        )}

        {/* Exhaust smoke when driving */}
        {isScrolling && (
          <g className="exhaust-smoke">
            <circle cx="12" cy="58" r="3" fill="#9CA3AF" opacity="0.6">
              <animate attributeName="cx" values="12;4;-4" dur="0.4s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.6;0.3;0" dur="0.4s" repeatCount="indefinite"/>
              <animate attributeName="r" values="3;5;7" dur="0.4s" repeatCount="indefinite"/>
            </circle>
            <circle cx="8" cy="54" r="2" fill="#9CA3AF" opacity="0.4">
              <animate attributeName="cx" values="8;0;-8" dur="0.5s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.4;0.2;0" dur="0.5s" repeatCount="indefinite"/>
              <animate attributeName="r" values="2;4;6" dur="0.5s" repeatCount="indefinite"/>
            </circle>
            {/* Extra smoke puffs when going fast */}
            {isFastScrolling && (
              <>
                <circle cx="5" cy="62" r="4" fill="#9CA3AF" opacity="0.5">
                  <animate attributeName="cx" values="5;-5;-15" dur="0.35s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.5;0.25;0" dur="0.35s" repeatCount="indefinite"/>
                  <animate attributeName="r" values="4;6;9" dur="0.35s" repeatCount="indefinite"/>
                </circle>
                <circle cx="10" cy="50" r="2" fill="#D1D5DB" opacity="0.4">
                  <animate attributeName="cx" values="10;2;-6" dur="0.45s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.4;0.2;0" dur="0.45s" repeatCount="indefinite"/>
                  <animate attributeName="r" values="2;4;6" dur="0.45s" repeatCount="indefinite"/>
                </circle>
              </>
            )}
          </g>
        )}
        
        {/* Delivery Scooter/Bike SVG */}
        <g fill="#1E1B4B">
          {/* Delivery Box */}
          <rect x="8" y="28" width="28" height="26" rx="4" fill="#1E1B4B"/>
          <rect x="10" y="30" width="24" height="22" rx="3" fill="#312E81"/>
          
          {/* Bike Body/Frame */}
          <path d="M36 48 Q52 36 68 48 L64 58 Q52 52 42 58 Z" fill="#1E1B4B"/>
          
          {/* Seat */}
          <ellipse cx="48" cy="42" rx="10" ry="5" fill="#1E1B4B"/>
          
          {/* Handlebar stem */}
          <rect x="62" y="32" width="6" height="18" rx="2" fill="#1E1B4B"/>
          
          {/* Handlebar */}
          <rect x="56" y="28" width="18" height="6" rx="3" fill="#1E1B4B"/>
          
          {/* Headlight - brighter when going fast */}
          <circle cx="72" cy="46" r="4" fill={isFastScrolling ? "#FDE047" : isScrolling ? "#FBBF24" : "#F59E0B"}>
            {isScrolling && (
              <animate attributeName="opacity" values="1;0.7;1" dur={isFastScrolling ? "0.1s" : "0.2s"} repeatCount="indefinite"/>
            )}
          </circle>
          {/* Headlight glow when fast */}
          {isFastScrolling && (
            <circle cx="72" cy="46" r="7" fill="#FDE047" opacity="0.3">
              <animate attributeName="r" values="7;9;7" dur="0.15s" repeatCount="indefinite"/>
              <animate attributeName="opacity" values="0.3;0.5;0.3" dur="0.15s" repeatCount="indefinite"/>
            </circle>
          )}
          
          {/* Front Wheel */}
          <g>
            <circle cx="76" cy="68" r="16" stroke="#1E1B4B" strokeWidth="6" fill="none"/>
            <circle cx="76" cy="68" r="6" fill="#1E1B4B"/>
            {/* Wheel spokes that spin when scrolling */}
            <g style={{ transformOrigin: '76px 68px' }}>
              {isScrolling ? (
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 76 68"
                  to="360 76 68"
                  dur={isFastScrolling ? "0.15s" : "0.3s"}
                  repeatCount="indefinite"
                />
              ) : null}
              <line x1="76" y1="56" x2="76" y2="62" stroke="#6B7280" strokeWidth="2"/>
              <line x1="76" y1="74" x2="76" y2="80" stroke="#6B7280" strokeWidth="2"/>
              <line x1="64" y1="68" x2="70" y2="68" stroke="#6B7280" strokeWidth="2"/>
              <line x1="82" y1="68" x2="88" y2="68" stroke="#6B7280" strokeWidth="2"/>
            </g>
          </g>
          
          {/* Rear Wheel */}
          <g>
            <circle cx="28" cy="68" r="16" stroke="#1E1B4B" strokeWidth="6" fill="none"/>
            <circle cx="28" cy="68" r="6" fill="#1E1B4B"/>
            {/* Wheel spokes that spin when scrolling */}
            <g style={{ transformOrigin: '28px 68px' }}>
              {isScrolling ? (
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 28 68"
                  to="360 28 68"
                  dur={isFastScrolling ? "0.15s" : "0.3s"}
                  repeatCount="indefinite"
                />
              ) : null}
              <line x1="28" y1="56" x2="28" y2="62" stroke="#6B7280" strokeWidth="2"/>
              <line x1="28" y1="74" x2="28" y2="80" stroke="#6B7280" strokeWidth="2"/>
              <line x1="16" y1="68" x2="22" y2="68" stroke="#6B7280" strokeWidth="2"/>
              <line x1="34" y1="68" x2="40" y2="68" stroke="#6B7280" strokeWidth="2"/>
            </g>
          </g>
          
          {/* Frame connectors */}
          <path d="M42 56 L28 68" stroke="#1E1B4B" strokeWidth="5" strokeLinecap="round"/>
          <path d="M58 56 L76 68" stroke="#1E1B4B" strokeWidth="5" strokeLinecap="round"/>
          
          {/* Exhaust pipe */}
          <rect x="24" y="52" width="12" height="5" rx="2" fill="#6B7280"/>
        </g>
      </svg>
    </div>
  );
};

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/l9mcn8gp_Gig%20Fleet%20Logo.png";
const HERO_BG_URL = "https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/ck8i9dyz_WhatsApp%20Image%202026-03-20%20at%204.44.46%20PM.jpeg";

// TikTok Icon
const TikTokIcon = ({ size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Social Links
const socialLinks = {
  facebook: "https://www.facebook.com/GigFleetManagement",
  instagram: "https://www.instagram.com/gigfleet",
  linkedin: "https://www.linkedin.com/company/gigfleet",
  twitter: "https://twitter.com/gigfleet",
  youtube: "https://www.youtube.com/@gigfleet",
  tiktok: "https://www.tiktok.com/@gigfleet"
};

// Navigation
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Our Services", href: "/#services" },
    { name: "About Us", href: "/#about" },
    { name: "The App", href: "/#app" },
    { name: "#MyLifeMatters", href: "https://www.mylifematters.co.za", external: true },
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Contact", href: "/#contact" }
  ];

  return (
    <>
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm py-3" : "bg-white py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="Gig Fleet Management - Empowering Gigpreneurs for a Sustainable Future in South Africa" 
                title="Gig Fleet Management"
                className="h-10 md:h-12 w-auto" 
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link, i) => (
                link.external ? (
                  <a
                    key={link.name}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="nav-pill"
                  >
                    {link.name}
                  </a>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`nav-pill ${i === 0 ? 'active' : ''}`}
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <a href="/#contact" className="btn-primary">
                Contact Us <ArrowRight size={18} />
              </a>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isOpen ? "active" : ""}`}>
        <button className="absolute top-6 right-6" onClick={() => setIsOpen(false)}>
          <X size={32} />
        </button>
        <img src={LOGO_URL} alt="Gig Fleet Management Logo" title="Gig Fleet Management" className="h-14 mb-8" />
        <div className="flex flex-col items-center gap-4">
          {navLinks.map((link) => (
            link.external ? (
              <a
                key={link.name}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xl font-medium text-gray-700 hover:text-[#1E1B4B]"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className="text-xl font-medium text-gray-700 hover:text-[#1E1B4B]"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            )
          ))}
          <a href="/#contact" className="btn-primary mt-4" onClick={() => setIsOpen(false)}>
            Contact Us
          </a>
        </div>
      </div>
    </>
  );
};

// Hero Section - Modern split layout
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="pt-10 lg:pt-16 pb-16 lg:pb-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-slideInLeft">
            <img 
              src="/rfa-proud-member.jpg"
              alt="Proud member of The Road Freight Association - Without Trucks, South Africa Stops! September 2026 - August 2027"
              title="Proud member of The Road Freight Association"
              data-testid="rfa-member-badge"
              className="w-full max-w-md rounded-xl shadow-md mb-5"
            />
            <span data-testid="green-eco-badge" className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide mb-5">
              <Leaf className="w-3.5 h-3.5" /> Green Gig Economy
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              <span className="text-[#1E1B4B]">Empowering</span> Gigpreneurs for a{" "}
              <span className="underline decoration-green-500 decoration-4 underline-offset-8">Sustainable Future</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
              Creating a New <span className="text-green-600 font-semibold">Green</span> Gig Economy and Social Compact for Independent Delivery Riders 
              and Empowering Riders through Micro Franchising.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <a href="/#services" className="btn-primary">
                Our Services <ArrowRight size={18} />
              </a>
              <a 
                href="https://wa.me/27660719068"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <MessageCircle size={18} /> Chat on WhatsApp
              </a>
            </div>

            <a href="/#contact" className="btn-outline">
              <Phone size={18} /> Contact Us
            </a>
          </div>

          {/* Right Image */}
          <div className="relative animate-slideInRight">
            <div className="rounded-image overflow-hidden shadow-2xl">
              <img 
                src={HERO_BG_URL}
                alt="Gig Fleet Delivery Network - Empowering Gigpreneurs across South African cities with micro franchising opportunities"
                title="Gig Fleet - Own Your Zone, Own Your Future"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            
            {/* Floating Badge */}
            <div className="hero-badge absolute -bottom-6 -left-6 lg:left-8">
              <div className="hero-badge-icon">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">B-BBEE Level 2</p>
                <p className="text-sm text-gray-500">125% Procurement</p>
              </div>
            </div>

            {/* Ethics over Profits Badge */}
            <div className="absolute -top-4 -right-4 lg:right-8 bg-white rounded-2xl p-3 shadow-lg border border-gray-100" data-testid="ethics-badge">
              <img 
                src="https://customer-assets.emergentagent.com/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/8e530xbs_ChatGPT%20Image%20Apr%202%2C%202026%2C%2007_09_17%20AM.png"
                alt="Ethics over Profits - Our core value at Gig Fleet Management"
                title="Ethics over Profits"
                className="h-12 md:h-14 w-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Data
const services = [
  { icon: Zap, title: "EV Bikes", description: "4500W electric bikes, ~110 km/h, up to 200-250 km daily range with dual battery mode." },
  { icon: BatteryCharging, title: "Battery Swopping Stations", description: "Swap a depleted battery for a full one in ~2 minutes. No downtime, no range anxiety." },
  { icon: Smartphone, title: "Airtime & Data", description: "3 Gigs, 300 Calls and 300 SMS's with a work mobile provided to separate work and private use." },
  { icon: Shield, title: "Insurance & Cover", description: "Critical Care cover for Private Hospital care up to R1.6 million." },
  { icon: Settings, title: "Bike Asset Management", description: "Ownership, leasing, and maintenance." },
  { icon: Wrench, title: "Maintenance", description: "Regular and emergency repair services." },
  { icon: Heart, title: "Mental Wellness", description: "Mentorship and Mental Health Support with weekly assessments done on the App for Health Check, Gigpreneur Training and Tools for bike management training." },
  { icon: GraduationCap, title: "Training & Support", description: "Road safety and Social Development, with a call centre supporting riders all the way. Assessments include personal Business Development training." },
  { icon: Cpu, title: "Technology", description: "Optimized routing, requesting Estate Access Codes and Live Tracking, with customers having access to the delivery rider for the last mile." }
];

// Electric Fleet Banner
const ElectricBanner = () => {
  const BannerContent = () => (
    <div className="flex items-center gap-3 flex-shrink-0 px-8">
      <span className="flex items-center gap-1.5">
        <Zap className="w-4 h-4 text-green-200" />
        <Leaf className="w-4 h-4 text-green-200" />
      </span>
      <p className="text-sm md:text-base font-bold text-white whitespace-nowrap">
        "A Fully Electric Fleet. A Fully Backed Rider."
      </p>
      <span className="text-green-300">|</span>
      <p className="text-xs md:text-sm text-green-100 whitespace-nowrap">
        Gig Fleet has completed its transition to <span className="font-semibold text-white">100% electric</span> — we're going green.
      </p>
    </div>
  );

  return (
    <section data-testid="electric-banner" className="bg-green-700 py-2.5 mt-[64px] md:mt-[72px] sticky top-[60px] md:top-[68px] z-40 overflow-hidden">
      <div className="banner-marquee flex w-max">
        <BannerContent />
        <BannerContent />
        <BannerContent />
        <BannerContent />
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" data-testid="services-section" className="py-12 lg:py-16 bg-gradient-soft">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="section-badge mb-3">Our Services</span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3 mb-4">
            Empowering Your Ride
          </h2>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            Everything you need to succeed as a Gigpreneur - from equipment to wellbeing support.
          </p>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-3 md:gap-4">
          {services.map((service, index) => (
            <div 
              key={index} 
              data-testid={`service-card-${index}`} 
              className="bg-white rounded-xl p-4 md:p-5 text-center border-2 border-[#1E1B4B] hover:shadow-lg transition-all"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 bg-[#C7D2FE] rounded-xl flex items-center justify-center mx-auto mb-3">
                <service.icon className="w-6 h-6 md:w-7 md:h-7 text-[#1E1B4B]" />
              </div>
              <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1">{service.title}</h3>
              <p className="text-xs md:text-sm text-gray-600 leading-snug">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  return (
    <section data-testid="mission-section" className="py-20 lg:py-28 bg-[#1E1B4B]">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-relaxed mb-8">
          "Empowering Gigpreneurs by providing comprehensive support, innovative solutions, 
          and a strong social compact to create a <span className="text-green-400">sustainable</span> and thriving gig economy."
        </h2>
        <p className="text-2xl md:text-3xl text-white font-semibold mb-8">
          Own Your <span className="text-yellow-300">Zone</span>, Own Your Future.
        </p>
        <a 
          href="https://wa.me/27660719068"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-white text-[#1E1B4B] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all"
        >
          <MessageCircle size={20} /> Need info? Let's chat
        </a>
      </div>
    </section>
  );
};

// EV Rental Flyer Section
const FLYER_IMG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/udm08ie3_ChatGPT%20Image%20Aug%2010%2C%202026%2C%2003_59_58%20PM.png";

const shareText = "Ride. Deliver. Earn. Grow. 🏍️⚡ Rent an EV bike with Gig Fleet — all-inclusive weekly rental with battery swops, insurance, tracker & more. Check out the flyer:";

const FlyerSection = () => {
  const shareUrl = FLYER_IMG_URL;
  return (
    <section data-testid="flyer-section" className="py-16 lg:py-20 bg-gradient-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img 
              src={FLYER_IMG_URL}
              alt="Gig Fleet EV Bike Rental - Ride, Deliver, Earn, Grow. All-inclusive weekly rental with EV bike, battery swops, insurance, tracker, maintenance and the Gig Fleet App"
              title="Rent an EV Bike. Power Your Future."
              className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
              loading="lazy"
            />
            <div className="flex items-center justify-center gap-3 mt-5">
              <span className="text-sm text-gray-500 flex items-center gap-1.5">
                <Share2 className="w-4 h-4" /> Share this flyer:
              </span>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-flyer-whatsapp"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-green-700 transition-colors"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-flyer-facebook"
                className="inline-flex items-center gap-2 bg-[#1877F2] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#0f5fce] transition-colors"
              >
                <Facebook size={16} /> Facebook
              </a>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="section-badge mb-4">EV Bike Rental</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-2">
              Ride. Deliver.
            </h2>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-6">
              Earn. Grow.
            </h2>
            <p className="text-lg text-gray-600 mb-4">
              Rent an EV bike and power your future. Gig Fleet provides everything you need 
              to hit the road, deliver more and earn better — all this, one simple price.
            </p>
            <p className="text-gray-600 mb-8">
              EV bike, 5-days battery swops, mobile with airtime & data, tracker, critical care 
              insurance, maintenance & services, and the Gig Fleet App. 
              <span className="font-semibold text-[#1E1B4B]"> Focus on deliveries. We take care of the rest.</span>
            </p>
            <a 
              href="https://wa.me/27686207281"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="flyer-whatsapp-cta"
              className="btn-primary"
            >
              <MessageCircle size={18} /> Apply via WhatsApp — 068 620 7281
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Team Data - All members
const teamMembers = [
  { name: "Maxwell Dlamini", role: "Fleet Manager", phone: "062 742 6320" },
  { name: "Boitumelo Raphahlelo", role: "CFO", phone: "081 443 2544" },
  { name: "Olefile Monyepao", role: "COO", phone: "069 402 2397" },
  { name: "Hulisani Ndou", role: "We Care Team Member", phone: "066 421 6850" },
  { name: "Siyabonga Mokwena", role: "Safety Officer", phone: "081 404 1603" },
  { name: "Sifiso Sibiya", role: "Gigpreneur Training Officer", phone: "079 473 2780" },
  { name: "Bafana Fuyana", role: "Collections Manager", phone: "076 668 3088" },
  { name: "Tumisa Ditha", role: "Information Technology Officer", phone: "060 308 9912" },
  { name: "Baby Matsiela", role: "Hub Human Resources Officer", phone: "084 860 9961" },
  { name: "Rosina Makhonkxa", role: "The Voice Behind Our Social Media", phone: "062 981 0305" },
  { name: "Thandiwe Nketshane", role: "Call Centre Manager", phone: "069 212 4714" },
  { name: "Portia Ralefatana", role: "Branding", phone: "060 130 8219" }
];

// Team Posters - members with uploaded posters
const teamPosters = [
  { 
    name: "Boitumelo Raphahlelo", 
    role: "Chief Financial Officer",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/3sydonf3_Boitumelo.png" 
  },
  { 
    name: "Bafana Fuyana", 
    role: "Collections Manager",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/2jcqad9q_Bafana.png" 
  },
  { 
    name: "Baby Matsiela", 
    role: "Hub Human Resources Officer",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/kla31inp_Baby%20HR.png" 
  },
  { 
    name: "Hulisani Ndou", 
    role: "We Care Team Member",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/vq97efm6_Hulisani.png" 
  },
  { 
    name: "Maxwell Dlamini", 
    role: "Fleet Manager",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/pwmhpny5_Njabulo.png" 
  },
  { 
    name: "Olefile Monyepao", 
    role: "Chief Operating Officer",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/407rezfy_Olefile.png" 
  },
  { 
    name: "Portia Ralefatana", 
    role: "Branded Delivery Operations Lead",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/9rdbd8rn_Portia.png" 
  },
  { 
    name: "Rosina Makhonkxa", 
    role: "The Voice Behind Our Social Media",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/qg8p9c9k_Rosina.png" 
  },
  { 
    name: "Sifiso Sibiya", 
    role: "Gigpreneur Training Officer",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/t1ztu24t_Sifiso.png" 
  },
  { 
    name: "Siyabonga Mokwena", 
    role: "Safety Officer",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/r3fc9gp0_Siya.png" 
  },
  { 
    name: "Thandiwe Nketshane", 
    role: "Call Centre Manager",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/hatmolqe_Thandiwe.png" 
  },
  { 
    name: "Tumisa Ditha", 
    role: "Information Technology Officer",
    url: "https://customer-assets-39nsmqrw.emergentagent.net/job_cfca131c-bfb4-43ad-9a43-0645c2f19845/artifacts/youh1hzn_Tumi.png" 
  }
];

// About Section - Team posters + remaining members
const AboutSection = () => {
  const [selectedPoster, setSelectedPoster] = useState(null);
  const posterNames = teamPosters.map(p => p.name);
  const remainingMembers = teamMembers.filter(m => !posterNames.includes(m.name));

  return (
    <section id="about" data-testid="about-section" className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
          <div>
            <span className="section-badge mb-3">Our Team</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-3">
              Meet The Team
            </h2>
          </div>
          <p className="text-gray-600 lg:max-w-md">
            The dedicated people behind Gig Fleet Management working to empower Gigpreneurs.
          </p>
        </div>

        {/* Team Posters */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-14">
          {teamPosters.map((poster, index) => (
            <div key={index}>
              <button
                data-testid={`team-poster-${index}`}
                onClick={() => setSelectedPoster(poster)}
                className="w-full text-left rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#1E1B4B]"
              >
                <img 
                  src={poster.url}
                  alt={`Meet ${poster.name} - ${poster.role} at Gig Fleet Management`}
                  title={`${poster.name} - ${poster.role} (click to enlarge)`}
                  className="w-full h-auto"
                  loading="lazy"
                />
              </button>
            </div>
          ))}
        </div>

        {/* Remaining Members */}
        {remainingMembers.length > 0 && (
          <>
            <p className="text-sm text-gray-500 mb-6 text-center">More team profiles coming soon.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {remainingMembers.map((member, index) => (
                <div 
                  key={index} 
                  data-testid={`team-member-${index}`} 
                  className="group text-center p-4 rounded-2xl hover:bg-[#E0E7FF] transition-all cursor-pointer"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#1E1B4B] to-[#4338CA] rounded-full mx-auto mb-3 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <span className="text-white font-bold text-lg">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 truncate">{member.name}</h4>
                  <p className="text-xs text-[#1E1B4B] mb-1">{member.role}</p>
                  <a 
                    href={`tel:${member.phone.replace(/\s/g, '')}`}
                    className="text-xs text-gray-400 hover:text-[#1E1B4B] transition-colors"
                  >
                    {member.phone}
                  </a>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Poster Lightbox */}
      {selectedPoster && (
        <div 
          data-testid="poster-lightbox"
          onClick={() => setSelectedPoster(null)}
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
        >
          <button
            data-testid="poster-lightbox-close"
            onClick={() => setSelectedPoster(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            aria-label="Close poster"
          >
            <X className="w-5 h-5 text-gray-900" />
          </button>
          <img 
            src={selectedPoster.url}
            alt={`${selectedPoster.name} - ${selectedPoster.role}`}
            className="max-h-[90vh] w-auto rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

// App Section
const AppSection = () => {
  const features = [
    { title: "Behavior Monitoring", desc: "Speeding alerts and compliance scoring." },
    { title: "Incident Reporting", desc: "Detailed logging of transgressions (e.g., No Helmet)." },
    { title: "The 5-Star Standard", desc: "Monthly rating systems for professional accountability." },
    { title: "Emergency Response", desc: "Integrated push notifications." }
  ];

  return (
    <section id="app" data-testid="app-section" className="py-16 lg:py-20 bg-[#E0E7FF]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - App Screenshot */}
          <div className="order-2 lg:order-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/wzgjljvv_image.png"
              alt="Gig Fleet App Safety and Accountability Framework - Mobile application for delivery rider management and compliance tracking"
              title="Gig Fleet Mobile App - Safety & Accountability"
              className="w-full max-w-lg mx-auto"
              loading="lazy"
            />
          </div>

          {/* Right - Content */}
          <div className="order-1 lg:order-2">
            <span className="section-badge mb-4">The App</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-2">
              Safety &
            </h2>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E1B4B] mb-6">
              Accountability Framework
            </h2>
            
            <ul className="space-y-5">
              {features.map((f, i) => (
                <li key={i} className="flex gap-4">
                  <div className="w-7 h-7 bg-[#1E1B4B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <span className="font-bold text-[#1E1B4B]">{f.title}:</span>
                    <span className="text-gray-700 ml-1">{f.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Operations Section - Admin Dashboard
const OperationsSection = () => {
  const features = [
    { title: "Real-Time Visibility", desc: "Monitor fleet operations, active users, and attendance live." },
    { title: "Operational Efficiency", desc: "Integrated sorting scans and address validation." },
    { title: "Compliance Tracking", desc: "Automated inspection logging and compliance rate monitoring." }
  ];

  return (
    <section id="operations" data-testid="operations-section" className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Dashboard Image */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/ji9krtb2_image.png"
              alt="Gig Fleet Admin Panel - Operations Control Dashboard for real-time fleet visibility, operational efficiency and compliance tracking"
              title="Gig Fleet Admin Dashboard - Operations Control"
              className="w-full"
              loading="lazy"
            />
          </div>

          {/* Right - Content */}
          <div>
            <span className="section-badge mb-4">Operations</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E1B4B] mt-4 mb-2">
              Admin &
            </h2>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Operations Control
            </h2>
            
            <ul className="space-y-5">
              {features.map((f, i) => (
                <li key={i} className="flex gap-4">
                  <div className="w-3 h-3 bg-[#1E1B4B] rounded-full flex-shrink-0 mt-2"></div>
                  <div>
                    <span className="font-bold text-[#1E1B4B]">{f.title}:</span>
                    <span className="text-gray-700 ml-1">{f.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Partners Section
const PartnersSection = () => {
  return (
    <section id="partners" data-testid="partners-section" className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="section-badge mb-4">Partners</span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
            Our Network
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Funders */}
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Funders</h3>
            <div className="bg-gray-50 rounded-2xl p-8 flex flex-col md:flex-row items-start justify-center gap-8">
              <div className="text-center md:text-left">
                <a href="https://www.drop24.co.za" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/r24jsqxw_Drop24%20White%20Logo%20Cropped.png"
                    alt="Drop24 Ethical Logistics - Gig Fleet Funding Partner providing structured order book and delivery demand through Service Level Agreement"
                    title="Drop24 Ethical Logistics - Visit Website"
                    className="h-12 md:h-14 w-auto object-contain mx-auto md:mx-0 hover:opacity-80 transition-opacity"
                    loading="lazy"
                  />
                </a>
                <p className="text-xs text-gray-600 mt-2 max-w-xs leading-relaxed">
                  <strong>Drop24 Ethical Logistics</strong> provides Gig Fleet with a structured order book and delivery demand through a Service Level Agreement, enabling consistent work, route density, and scalable last-mile operations.
                </p>
              </div>
            </div>
          </div>

          {/* Tech Partners */}
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Tech Partners</h3>
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center justify-items-center mb-6">
                <a href="https://www.bobgo.co.za" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/1u73vj9y_image.png"
                    alt="Bob Go (BobGo) - Technology partner powering Gig Fleet order integration and last-mile logistics"
                    title="Bob Go - Visit Website"
                    className="h-10 w-auto object-contain hover:opacity-80 transition-opacity"
                    loading="lazy"
                  />
                </a>
                <a href="https://www.bobbox.co.za" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/74ueekx4_image.png"
                    alt="Bob Box (BobBox) - Technology partner providing fulfilment solutions for Gig Fleet"
                    title="Bob Box - Visit Website"
                    className="h-10 w-auto object-contain hover:opacity-80 transition-opacity"
                    loading="lazy"
                  />
                </a>
                <a href="https://www.bobpay.co.za" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/lvci2cde_image.png"
                    alt="Bob Pay (BobPay) - Technology partner enabling seamless payments for Gig Fleet"
                    title="Bob Pay - Visit Website"
                    className="h-10 w-auto object-contain hover:opacity-80 transition-opacity"
                    loading="lazy"
                  />
                </a>
                <a href="https://www.shiplogic.com" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_gig-fleet-mgmt/artifacts/8cqmnxgj_image.png"
                    alt="Ship Logic (ShipLogic) - Technology partner for last-mile logistics integration with Gig Fleet"
                    title="Ship Logic - Visit Website"
                    className="h-10 w-auto object-contain hover:opacity-80 transition-opacity"
                    loading="lazy"
                  />
                </a>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed max-w-md mx-auto">
                <strong>Bob Go</strong>, <strong>Bob Pay</strong>, <strong>Bob Box</strong>, and <strong>Ship Logic</strong> power Gig Fleet's technology ecosystem, enabling seamless order integration, payments, fulfilment, and last-mile logistics at scale.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// MyLifeMatters Section
const MyLifeMattersSection = () => {
  const pillars = [
    { 
      title: "WeCare", 
      desc: "Rider wellbeing, support systems, accountability, daily engagement, and safety culture.",
      color: "bg-red-500"
    },
    { 
      title: "Thrive", 
      desc: "Entrepreneurial development, training, assignments, personal growth, and business discipline.",
      color: "bg-green-500"
    },
    { 
      title: "Tools", 
      desc: "Gig Fleet App, AI-driven assignments, training modules, operational reporting, and business tools.",
      color: "bg-blue-500"
    }
  ];

  return (
    <section id="mylifematters" data-testid="mylifematters-section" className="py-16 lg:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
            THE MOVEMENT
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-[#1E1B4B]">#</span>MyLifeMatters
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Creating a New Green Gig Economy and Social Compact for Gigpreneurs. 
            Building awareness around the lives, safety, and economic dignity of delivery riders.
          </p>
        </div>

        {/* Why This Matters */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold mb-6">
              Why This <span className="text-[#1E1B4B]">Matters</span>
            </h3>
            <p className="text-gray-300 mb-6">
              Delivery bike riders are the backbone of South Africa's growing gig economy. 
              Yet they work as independent contractors without safety nets, structured support, 
              or a clear path to economic stability.
            </p>
            <ul className="space-y-4">
              {[
                "Rider safety concerns on dangerous roads",
                "Lack of medical benefits or insurance",
                "No structured training or support",
                "No clear pathway to ownership or growth"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="bg-gradient-to-br from-[#1E1B4B]/20 to-purple-600/20 rounded-3xl p-8 backdrop-blur-sm border border-white/10">
              <div className="text-center">
                <div className="text-6xl font-bold text-[#1E1B4B] mb-2">Safety</div>
                <div className="text-2xl text-gray-300">First</div>
                <p className="text-gray-400 mt-4">Every rider matters</p>
              </div>
            </div>
          </div>
        </div>

        {/* Three Pillars */}
        <div className="mb-16">
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-10">
            Three Pillars of <span className="text-[#1E1B4B]">Success</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((pillar, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-all">
                <div className={`w-12 h-12 ${pillar.color} rounded-xl flex items-center justify-center mb-4`}>
                  <span className="text-white font-bold text-lg">{i + 1}</span>
                </div>
                <h4 className="text-xl font-bold text-white mb-3">{pillar.title}</h4>
                <p className="text-gray-400 text-sm">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-[#1E1B4B]/20 to-purple-600/20 rounded-3xl p-8 md:p-12 border border-white/10">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Join the <span className="text-[#1E1B4B]">#MyLifeMatters</span> Movement
          </h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Whether you're a corporate looking to make an impact, a rider seeking opportunity, 
            or someone who believes in our cause — there's a place for you in this movement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://www.mylifematters.co.za" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Learn More <ArrowRight size={18} />
            </a>
            <a 
              href="https://wa.me/27660719068"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-full font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
            >
              <MessageCircle size={18} /> Chat With Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "", id_number: "", contact_number: "", email: "", experience: "", reason: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/applications`, formData);
      setSuccess(true);
      setFormData({ name: "", id_number: "", contact_number: "", email: "", experience: "", reason: "" });
    } catch (err) {
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-28 bg-gradient-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <span className="section-badge mb-4">Get In Touch</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
              Contact Us
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Ready to join the Gig Fleet family? Get in touch with us today.
            </p>
            
            <div className="space-y-6 mb-10">
              <a 
                href="https://wa.me/27660719068"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-contact"
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#C7D2FE] rounded-2xl flex items-center justify-center group-hover:bg-[#1E1B4B] transition-colors">
                  <Phone className="w-6 h-6 text-[#1E1B4B] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">WhatsApp Us</p>
                  <p className="text-gray-600">066 071 9068</p>
                </div>
              </a>

              <a 
                href="mailto:andrew@gigfleet.co.za,olefile@gigfleet.co.za"
                data-testid="email-contact"
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#C7D2FE] rounded-2xl flex items-center justify-center group-hover:bg-[#1E1B4B] transition-colors">
                  <Mail className="w-6 h-6 text-[#1E1B4B] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Email</p>
                  <p className="text-gray-600">andrew@gigfleet.co.za</p>
                  <p className="text-gray-600">olefile@gigfleet.co.za</p>
                </div>
              </a>
            </div>

            {/* Social Links */}
            <div>
              <p className="font-semibold text-gray-900 mb-4">Follow Us</p>
              <div className="flex gap-3">
                {[
                  { icon: Facebook, link: socialLinks.facebook, live: true },
                  { icon: Instagram, link: socialLinks.instagram },
                  { icon: Linkedin, link: socialLinks.linkedin },
                  { icon: Twitter, link: socialLinks.twitter },
                  { icon: Youtube, link: socialLinks.youtube },
                  { icon: TikTokIcon, link: socialLinks.tiktok }
                ].map((social, i) => (
                  <a 
                    key={i}
                    href={social.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                      social.live 
                        ? "bg-green-600 text-white hover:bg-green-700" 
                        : "bg-[#C7D2FE] text-[#1E1B4B] hover:bg-[#1E1B4B] hover:text-white"
                    }`}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
              <a 
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="facebook-follow-nudge"
                className="mt-5 inline-flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl px-5 py-3 hover:bg-green-100 transition-colors group"
              >
                <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Facebook className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Follow us on Facebook</p>
                  <p className="text-xs text-green-700">Rider stories, updates & opportunities — @GigFleetManagement</p>
                </div>
              </a>
            </div>
          </div>

          {/* Application Form */}
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Application Form for Riders</h3>
            
            {success ? (
              <div data-testid="form-success" className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Application Submitted!</h4>
                <p className="text-gray-600 mb-4">We'll be in touch soon.</p>
                <button onClick={() => setSuccess(false)} className="text-[#1E1B4B] font-medium hover:underline">
                  Submit another application
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="rider-application-form" className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Name and Surname *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required className="form-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">ID Number *</label>
                  <input type="text" name="id_number" value={formData.id_number} onChange={handleChange} required className="form-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Contact Number *</label>
                  <input type="tel" name="contact_number" value={formData.contact_number} onChange={handleChange} required className="form-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="form-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Experience and availability *</label>
                  <textarea name="experience" value={formData.experience} onChange={handleChange} required rows={3} className="form-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Why do you want to Join? *</label>
                  <textarea name="reason" value={formData.reason} onChange={handleChange} required rows={3} className="form-input" />
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center">
                  {loading ? "Submitting..." : "Submit Application"} {!loading && <Send size={18} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-gray-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Gig Fleet Management - B-BBEE Level 2 Fleet Management Company for Gigpreneurs in South Africa" 
              title="Gig Fleet Management"
              className="h-12 mb-4 bg-white p-2 rounded-lg" 
            />
            <p className="text-gray-400 mb-6 max-w-md">
              Empowering Gigpreneurs for a Sustainable Future. Creating a New Green Gig Economy 
              and Social Compact for Independent Delivery Riders.
            </p>
            <p data-testid="footer-green-tagline" className="flex items-center gap-2 text-green-400 text-sm mb-6">
              <Leaf className="w-4 h-4" /> Proudly building a greener last-mile, one ride at a time.
            </p>
            <div className="flex gap-3">
              {[
                { icon: Facebook, link: socialLinks.facebook, name: "facebook", live: true },
                { icon: Instagram, link: socialLinks.instagram, name: "instagram" },
                { icon: Linkedin, link: socialLinks.linkedin, name: "linkedin" },
                { icon: Twitter, link: socialLinks.twitter, name: "twitter" },
                { icon: Youtube, link: socialLinks.youtube, name: "youtube" },
                { icon: TikTokIcon, link: socialLinks.tiktok, name: "tiktok" }
              ].map((social, i) => (
                <a key={i} href={social.link} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${social.name}`} className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                  social.live 
                    ? "bg-green-600 text-white hover:bg-green-500" 
                    : "bg-gray-800 text-gray-400 hover:bg-[#1E1B4B] hover:text-white"
                }`}>
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3 text-gray-400">
              <li><a href="/#services" className="hover:text-white">Our Services</a></li>
              <li><a href="/#about" className="hover:text-white">About Us</a></li>
              <li><a href="/#app" className="hover:text-white">The App</a></li>
              <li><a href="https://www.mylifematters.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white">#MyLifeMatters</a></li>
              <li><a href="/#partners" className="hover:text-white">Partners</a></li>
              <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-gray-400">
              <li>
                <a href="https://wa.me/27660719068" className="hover:text-white flex items-center gap-2">
                  <Phone size={16} /> 066 071 9068
                </a>
              </li>
              <li>
                <a href="mailto:andrew@gigfleet.co.za" className="hover:text-white flex items-center gap-2">
                  <Mail size={16} /> andrew@gigfleet.co.za
                </a>
              </li>
              <li>
                <a href="mailto:olefile@gigfleet.co.za" className="hover:text-white flex items-center gap-2">
                  <Mail size={16} /> olefile@gigfleet.co.za
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Gig Fleet Management. All rights reserved.</p>
          <p className="mt-2">B-BBEE Level 2 Contributor | 125% Procurement Recognition</p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/27660719068"
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-float"
    className="whatsapp-float"
  >
    <span className="icon">
      <MessageCircle size={14} className="text-white" />
    </span>
    Chat with us
  </a>
);

// Blog Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then(res => setPosts(res.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-24 pb-20 min-h-screen bg-gradient-soft">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="section-badge mb-4">Blog</span>
          <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Latest News</h1>
          <p className="text-gray-600">Updates from Gig Fleet Management</p>
        </div>

        {loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1E1B4B] border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="blog-card">
                {post.image_url && <img src={post.image_url} alt={post.title} className="w-full h-48 object-cover" />}
                <div className="p-6">
                  <div className="flex gap-2 mb-3">
                    {post.tags?.slice(0, 2).map((tag, i) => (
                      <span key={i} className="text-xs bg-[#C7D2FE] text-[#1E1B4B] px-2 py-1 rounded-full">{tag}</span>
                    ))}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">{post.title}</h2>
                  <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center gap-1"><User size={14} /> {post.author}</span>
                    <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Blog Post Page
const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`).then(res => setPost(res.data)).catch(() => navigate("/blog")).finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) return <div className="pt-24 pb-20 min-h-screen flex justify-center items-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1E1B4B] border-t-transparent"></div></div>;
  if (!post) return null;

  return (
    <article className="pt-24 pb-20 min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4">
        <Link to="/blog" className="text-[#1E1B4B] hover:underline flex items-center gap-1 mb-6">
          <ArrowRight className="rotate-180" size={16} /> Back to Blog
        </Link>
        {post.image_url && <img src={post.image_url} alt={post.title} className="w-full h-64 md:h-96 object-cover rounded-2xl mb-8" />}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>
        <div className="flex items-center gap-4 text-gray-500 mb-8 pb-8 border-b">
          <span className="flex items-center gap-1"><User size={16} /> {post.author}</span>
          <span className="flex items-center gap-1"><Calendar size={16} /> {new Date(post.created_at).toLocaleDateString()}</span>
        </div>
        <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />
      </div>
    </article>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  return (
    <div className="pt-24 pb-20 min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="section-badge mb-4">Legal</span>
          <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Gig Fleet Management ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website gigfleet.co.za, use our mobile application, or engage with our services.
            </p>
            <p className="text-gray-700">
              Please read this privacy policy carefully. By using our services, you consent to the practices described in this policy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
            <p className="text-gray-700 mb-4">We may collect personal information that you voluntarily provide, including:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Name and surname</li>
              <li>South African ID number</li>
              <li>Contact number</li>
              <li>Email address</li>
              <li>Employment history and experience</li>
              <li>Vehicle/bike registration details</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Automatically Collected Information</h3>
            <p className="text-gray-700 mb-4">When you use our services, we may automatically collect:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Device information (type, operating system)</li>
              <li>IP address and location data</li>
              <li>Usage data and app analytics</li>
              <li>GPS/location tracking data (for delivery operations)</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use the collected information for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Processing rider applications and onboarding</li>
              <li>Managing fleet operations and deliveries</li>
              <li>Providing safety and compliance monitoring</li>
              <li>Communicating important updates and notifications</li>
              <li>Processing payments and financial transactions</li>
              <li>Improving our services and user experience</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing</h2>
            <p className="text-gray-700 mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Service Partners:</strong> Drop24, Bob Go, Ship Logic, and other logistics partners for delivery operations</li>
              <li><strong>Insurance Providers:</strong> For coverage and claims processing</li>
              <li><strong>Technology Partners:</strong> For app functionality and tracking services</li>
              <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We do not sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Encrypted data transmission (SSL/TLS)</li>
              <li>Secure data storage systems</li>
              <li>Access controls and authentication</li>
              <li>Regular security assessments</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights (POPIA Compliance)</h2>
            <p className="text-gray-700 mb-4">
              Under the Protection of Personal Information Act (POPIA), you have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Access your personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information</li>
              <li>Object to the processing of your information</li>
              <li>Lodge a complaint with the Information Regulator</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, please contact us at <a href="mailto:andrew@gigfleet.co.za" className="text-[#1E1B4B] hover:underline">andrew@gigfleet.co.za</a> or <a href="mailto:olefile@gigfleet.co.za" className="text-[#1E1B4B] hover:underline">olefile@gigfleet.co.za</a>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies and Tracking</h2>
            <p className="text-gray-700 mb-4">
              Our website may use cookies and similar tracking technologies to enhance your experience. You can control cookie settings through your browser preferences.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Links</h2>
            <p className="text-gray-700">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read their privacy policies.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-[#E0E7FF] rounded-2xl p-6">
              <p className="text-gray-800 mb-2"><strong>Gig Fleet Management</strong></p>
              <p className="text-gray-700 mb-1">Email: <a href="mailto:andrew@gigfleet.co.za" className="text-[#1E1B4B] hover:underline">andrew@gigfleet.co.za</a> / <a href="mailto:olefile@gigfleet.co.za" className="text-[#1E1B4B] hover:underline">olefile@gigfleet.co.za</a></p>
              <p className="text-gray-700">WhatsApp: <a href="https://wa.me/27660719068" className="text-[#1E1B4B] hover:underline">066 071 9068</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// Home Page
const HomePage = () => (
  <>
    <ElectricBanner />
    <HeroSection />
    <ServicesSection />
    <MissionSection />
    <FlyerSection />
    <AboutSection />
    <AppSection />
    <OperationsSection />
    <MyLifeMattersSection />
    <PartnersSection />
    <ContactSection />
  </>
);

// Main App
function App() {
  return (
    <div className="App">
      <CustomCursor />
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
